import { z } from 'zod';
import { ContentBase64Url, EncryptedContent } from './content.js';
export const PrivateEntityKind = z.enum([
    'project',
    'workstream',
    'task',
    'rule',
    'idea',
    // ---- screen-sweep: encrypted artifact index (value: ArtifactIndexEntry) ----
    'artifact',
    // ---- end screen-sweep ----
    // ---- live-activity: the user's run rename (value: RunTitleEntry) ----
    'runtitle',
    // ---- end live-activity ----
    // ---- g10-inventory: per-project stack inventory (value: ProjectInventory) ----
    'inventory',
    // ---- end g10-inventory ----
    // ---- bark-triage: barked items (value: BarkRecord, bark.ts) ----
    'bark',
    // ---- end bark-triage ----
    // ---- g9-feedback: per-project feedback sources (FeedbackConfigRecord) and proposals ----
    'feedbackcfg',
    'feedback',
    // ---- end g9-feedback ----
    // ---- g13-digests: last-visited marker per project (LastVisitEntry) + synced settings ----
    'visit',
    'setting',
    // ---- end g13-digests ----
    // ---- phase2-decisions: agent asks with threads (value: DecisionRecord, decision.ts) ----
    'decision',
    // ---- end phase2-decisions ----
    // ---- lanes-integrate: per-project goal + milestones (GoalRecord, goals.ts) ----
    'goal',
    // ---- end lanes-integrate ----
    // ---- g8-voice: research library entries (value: ResearchRecord, voice.ts) ----
    'research',
    // ---- end g8-voice ----
    // ---- ingest-sync: G16 ingest config/status/decisions and items (ingest.ts) ----
    'ingestcfg',
    'ingest',
    // ---- end ingest-sync ----
    // ---- viko-call: one record per call (actions taken "via call"; value: CallRecord, call.ts) ----
    'call',
    // ---- end viko-call ----
]);
export const PrivateEntityId = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
const Revision = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Digest = ContentBase64Url.length(43);
export const EntityContent = EncryptedContent.refine((c) => PrivateEntityKind.safeParse(c.context.kind).success &&
    PrivateEntityId.safeParse(c.context.objectId).success &&
    c.context.runId === `entity:${c.context.kind}`, 'invalid entity context');
export const EntityPut = z.strictObject({ content: EntityContent, previous: Digest.nullable() });
export const EntityHead = z.strictObject({
    id: PrivateEntityId,
    kind: PrivateEntityKind,
    revision: Revision.positive(),
    digest: Digest,
});
export const EntityListRequest = z.strictObject({
    kind: PrivateEntityKind,
    after: PrivateEntityId.optional(),
});
export const EntityListPage = z.strictObject({
    items: z.array(EntityHead).max(32),
    next: PrivateEntityId.optional(),
});
/**
 * read-budget: every entity head of every kind in one call (browser page loads), ordered by
 * `kind:id`. `since` is the account's entity version from an earlier answer: while no entity
 * changed, the relay answers `{ version, unchanged: true }` with no items.
 */
export const ENTITY_HEADS_PAGE = 256;
export const EntityHeadsCursor = z
    .string()
    .max(64)
    .refine((c) => {
    const i = c.indexOf(':');
    return (i > 0 &&
        PrivateEntityKind.safeParse(c.slice(0, i)).success &&
        PrivateEntityId.safeParse(c.slice(i + 1)).success);
}, 'invalid entity cursor');
export const EntityHeadsRequest = z.strictObject({
    after: EntityHeadsCursor.optional(),
    since: Revision.optional(),
});
export const EntityHeadsPage = z.strictObject({
    version: Revision,
    items: z.array(EntityHead).max(ENTITY_HEADS_PAGE).default([]),
    next: EntityHeadsCursor.optional(),
    unchanged: z.literal(true).optional(),
});
export const ENTITY_HEADS_PAGE_BYTES = 65_536;
export const EntityReadRequest = z.strictObject({
    kind: PrivateEntityKind,
    id: PrivateEntityId,
    after: Revision,
});
export const EntityReadPage = z.strictObject({
    head: EntityHead,
    records: z.array(EntityContent).max(16),
});
/** These fields are inside AEAD; the relay cannot edit the revision ancestry or value. */
export const EntityPlaintext = z.strictObject({
    version: z.literal(1),
    previous: Digest.nullable(),
    value: z.record(z.string(), z.unknown()),
});
export const ENTITY_RECORD_BYTES = 256_000;
export const ENTITY_PAGE_BYTES = 16 * ENTITY_RECORD_BYTES + 4096;
const RpcId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
/** Approved daemon RPCs. A schema match is required before the ciphertext scrub bypass. */
export const EntityRpcFrame = z.discriminatedUnion('method', [
    z.strictObject({
        type: z.literal('rpc'),
        id: RpcId,
        method: z.literal('content.entity.put'),
        params: EntityPut,
    }),
    z.strictObject({
        type: z.literal('rpc'),
        id: RpcId,
        method: z.literal('content.entity.list'),
        params: EntityListRequest,
    }),
    z.strictObject({
        type: z.literal('rpc'),
        id: RpcId,
        method: z.literal('content.entity.read'),
        params: EntityReadRequest,
    }),
]);
//# sourceMappingURL=encrypted-entities.js.map